import { Injectable } from '@angular/core';
import { HttpClientService } from '../common/http-client.service';

export interface ConflictSearchCriteria {
  name?: string;
  ssn?: string;
  ein?: string;
  phone?: string;
  email?: string;
  matter_id?: number;
  notify?: boolean;
}

export interface ConflictResult {
  contact_id: number;
  contact_name: string;
  matter_id: number;
  matter_number: string;
  matter_name: string;
  matter_status: string;
  roles: string[];
  involvement_id: number;
  involvement_created_at: string;
  archived: boolean;
}

export interface SearchedContact {
  id: number;
  name: string;
  display_name: string;
  ssn: string;
  ein: string;
  matched_on: string[];
}

export interface ConflictCheck {
  id: number;
  user_id: number;
  user_name: string;
  matter_id: number;
  matter_number: string;
  matter_name: string;
  conflicts_found: number;
  created_at: string;
  has_conflicts: boolean;
  approved: boolean;
  approved_by_id: number;
  approved_by_name: string;
  approved_at: string;
  needs_approval: boolean;
  searched_contacts?: SearchedContact[];
  conflicts?: ConflictResult[];
  search_criteria?: any;
}

export interface MatterConflictStatus {
  has_unapproved_conflicts: boolean;
  latest_check: ConflictCheck | null;
  frozen: boolean;
}

@Injectable()
export class ConflictService {
  private conflictsURL = 'conflicts';

  constructor(private httpClient: HttpClientService) {}

  /**
   * Get recent conflict checks
   * @return {Promise<any>} Server response with recent checks
   */
  getRecentChecks(): Promise<any> {
    return this.httpClient.get(`${this.conflictsURL}.json`);
  }

  /**
   * Get a specific conflict check by ID
   * @param {number} id Conflict check ID
   * @return {Promise<any>} Server response with conflict check details
   */
  getConflictCheck(id: number): Promise<any> {
    return this.httpClient.get(`${this.conflictsURL}/${id}.json`);
  }

  /**
   * Run a conflict search
   * @param {ConflictSearchCriteria} criteria Search criteria
   * @return {Promise<any>} Server response with search results
   */
  search(criteria: ConflictSearchCriteria): Promise<any> {
    return this.httpClient.post(`${this.conflictsURL}/search.json`, criteria);
  }

  /**
   * Check a specific matter for conflicts
   * @param {number} matterId Matter ID to check
   * @param {boolean} notify Whether to send notifications
   * @return {Promise<any>} Server response with conflict results
   */
  checkMatter(matterId: number, notify: boolean = false): Promise<any> {
    return this.httpClient.post(`${this.conflictsURL}/search.json`, {
      matter_id: matterId,
      notify: notify
    });
  }

  /**
   * Send notifications for an existing conflict check
   * @param {number} id Conflict check ID
   * @return {Promise<any>} Server response
   */
  notify(id: number): Promise<any> {
    return this.httpClient.post(`${this.conflictsURL}/${id}/notify.json`, {});
  }

  /**
   * Approve conflicts for a conflict check
   * @param {number} id Conflict check ID
   * @return {Promise<any>} Server response
   */
  approve(id: number): Promise<any> {
    return this.httpClient.post(`${this.conflictsURL}/${id}/approve.json`, {});
  }

  /**
   * Get conflict status for a matter
   * @param {number} matterId Matter ID
   * @return {Promise<MatterConflictStatus>} Status including frozen state
   */
  getMatterStatus(matterId: number): Promise<MatterConflictStatus> {
    return this.httpClient.get(`${this.conflictsURL}/matter_status.json?matter_id=${matterId}`);
  }
}
